/**
 * THE SURVEY MAP — the whole job as one tree, derived, never stored (D-028).
 *
 * From the design authority's walkthrough, 2026-08-30:
 *
 *   "Navigation is not global. The surveyor should be able to open navigation
 *    from ANY point in the survey, in one gesture, and land anywhere in one
 *    tap."
 *
 * This module is that navigation's single source: `mapModel()` derives the
 * tree — survey-level rows, one collapsible branch per machine room, lamps,
 * note counts, the progress ring — from the engines' own output.
 * The docked map bar, the full-height sheet, and the `sections` landing page
 * all render THIS model ("one function, read twice"), the same way every lamp
 * in the app reads `SectionProgress` rather than forming a second opinion.
 *
 * ── What the lamps are ──────────────────────────────────────────────────────
 * A row's lamp is the gap engine speaking: `done` is `SectionProgress.status`
 * verbatim; `here` is the current route (where the surveyor is STANDING, not
 * what is next — the wizard's "next" lives on the walk buttons); everything
 * else is hollow. One exception, and it is the submit gate's own rule
 * (guide.ts): a section that asks nothing — Ad Hoc — can never leave `todo`
 * by answering, so walked-ness turns it green, exactly as walked-ness
 * satisfies the gate. (A red `flag` lamp painted a `surveyor_flagged` gap off
 * the record; the card that raised one left with D-033 R15 and live results
 * stopped reading the record with round 8, task 1, so there is no such lamp.)
 *
 * ── Why in-progress rows carry a reason line ────────────────────────────────
 * D-021: *"Even though 19 of 19 questions have been answered, I don't know
 * what I'm doing wrong, but I'm stuck."* A lamp that is neither green nor
 * hollow explains itself in one line — the blocker's own headline — and a
 * green or untouched row stays quiet (D-014: nothing is called wrong before
 * the surveyor could have acted).
 * D-037 declutters the MAP itself — the reason no longer prints there — but
 * the model still derives it, so Close & Next and the last look keep saying
 * the same sentence the map used to.
 *
 * ── FLS is a survey-level row here ──────────────────────────────────────────
 * The hall indicator repeated Fire Life Safety at the foot of every room's
 * menu (D-027) because each room's menu was all the navigation there was. The
 * map shows the whole survey at once, so FLS appears exactly once, after the
 * room branches, where the entity model puts it. The submit gate — walk FLS
 * before closing — is untouched.
 *
 * Pure: no React, no store, no clock. `tests/unit/map-tree.test.ts` runs it
 * directly.
 */

import type { SectionInstance, SurveyDocument } from '../../core/types';
import { sectionInstanceId } from '../../core/types';
import { hrefFor, type Route } from '../routing';
import { pendingRoomSlots } from './buildingSetup';
import { guidePlan } from './guide';
import { primaryBlocker, type SectionProgress, type SurveyResults } from './results';

/* ─────────────────────────────── the pieces ─────────────────────────────── */

export type MapLamp = 'done' | 'here' | 'todo';

export interface MapRowModel {
  /** `sectionInstanceId`, the literal `'submit'`, or `pending:<roomId>` for a declared room not yet created. */
  key: string;
  /** A real href — rows are anchors, never onClick (house rule). */
  href: string;
  /** Surveyor-facing, e.g. "Pit · Hoistway 2", "Review & Submit". */
  title: string;
  lamp: MapLamp;
  /**
   * One line, on in-progress rows (D-021's sentence; a "flagged" row has not
   * existed since round 8 ruling 1 retired the surveyor flag from live
   * results). Derived still; NOT printed by the map since D-037 — the map is
   * lamp + title, and the sentence is read at Close & Next and on the last look.
   */
  reason?: string;
  /** Notes filed on this section — section-level and question-level. */
  notes: number;
  /** True on the row the current route renders. */
  current: boolean;
  /**
   * The engine's verdict, independent of where the surveyor stands: the row
   * you are ON reads `here` for its lamp but still counts as done for the
   * branch's `done/total` and the ring once its status is done.
   */
  done: boolean;
}

export interface MapBranchModel {
  roomId: string;
  name: string;
  /** 1-based, among the machine rooms on the document. */
  position: number;
  rows: MapRowModel[];
  done: number;
  total: number;
}

export type MapNode =
  | { kind: 'row'; row: MapRowModel }
  | { kind: 'branch'; branch: MapBranchModel };

export interface MapModel {
  surveyTitle: string;
  nodes: MapNode[];
  /** The ring: done rows over every row except Review & Submit. */
  progress: { done: number; total: number; pct: number };
  /** The docked bar: the section as the hero, the survey · room as its subtitle. */
  bar: { here: string; place: string; room: string };
  currentKey: string | null;
  /** `guidePlan().ready` — whether the close screen would offer Submit. */
  ready: boolean;
}

/** Shared with the sections landing page — one label map, not two. */
export const ROOM_TYPE_LABEL: Record<string, string> = {
  traction: 'Traction',
  hydro: 'Hydro',
  mrl: 'MRL · Hydro set',
};

/**
 * "Machine Room 2" → "MR 2" on the bar's one subtitle line (D-037) — *"when
 * the building name is very long, the full text 'Machine Room 1' … can
 * overflow … e.g., KILBRIDE WAY CT - MR 1."* Only the default name pattern;
 * a surveyor's own room name is theirs and is printed whole.
 */
export function shortRoomName(name: string): string {
  const trimmed = name.trim();
  const match = /^machine room (\d+)$/i.exec(trimmed);
  return match ? `MR ${match[1] ?? ''}` : trimmed;
}

/* ─────────────────────────────── derivation ─────────────────────────────── */

function plural(count: number, word: string): string {
  return `${String(count)} ${word}${count === 1 ? '' : 's'}`;
}

/** Which row the route has the surveyor standing on. */
function currentKeyFor(route: Route): string | null {
  switch (route.name) {
    case 'section':
      return route.sectionId;
    case 'roomSetup':
      return sectionInstanceId('room_setup', { roomId: route.roomId });
    case 'building':
      return sectionInstanceId('building');
    case 'submit':
      return 'submit';
    default:
      return null;
  }
}

/**
 * The one line under a lamp that is neither green nor hollow: an in-progress
 * section speaks in the blocker's headline — the same sentence the walk guard
 * prints at Close & Next and the last look prints per row, because it is the
 * same `SectionBlocker`. (A flagged section used to speak in the surveyor's
 * own words here; the flag retired from live results with round 8, task 1,
 * and those words are the section's note.)
 *
 * Exported because it is D-021's third surface: `tests/unit/smith-street.test.ts`
 * and `blockers.test.ts` assert the walk guard, the last look and this lamp
 * all say one sentence, and this is the lamp's half of that promise.
 */
export function mapRowReason(progress: SectionProgress | undefined): string | undefined {
  if (!progress) return undefined;
  if (progress.status !== 'in_progress') return undefined;
  const blocker = primaryBlocker(progress);
  if (blocker) return blocker.headline;
  const left = progress.total - progress.answered;
  return left > 0 ? `${plural(left, 'question')} still to answer` : undefined;
}

function rowFor(args: {
  doc: SurveyDocument;
  results: SurveyResults;
  walked: ReadonlySet<string>;
  section: SectionInstance;
  currentKey: string | null;
  notesBySection: ReadonlyMap<string, number>;
}): MapRowModel {
  const { doc, results, walked, section, currentKey } = args;
  const progress = results.progressBySection.get(section.id);
  const status = progress?.status ?? 'todo';
  const current = section.id === currentKey;

  // Asks-nothing sections (Ad Hoc) can never answer their way to green, and
  // `isWalked` deems them walked unconditionally (clause 3 — so they never
  // block a submit). For the MAP the honest signal is the device's own trail:
  // green once the surveyor has actually been shown the screen, hollow before.
  const done =
    status === 'done' || ((progress?.total ?? 0) === 0 && walked.has(section.id));

  let lamp: MapLamp;
  if (current) lamp = 'here';
  else if (done) lamp = 'done';
  else lamp = 'todo';

  const reason = mapRowReason(progress);

  // The building section's home is the building SCREEN — the room list, the
  // B12 guard, the slots — not the generic section route (which also renders
  // it, and is what the walk's Previous from Room Setup reaches). The crumb
  // this row replaces went to the screen; so does the row.
  const href =
    section.kind === 'building'
      ? hrefFor({ name: 'building', surveyId: doc.surveyId })
      : hrefFor({ name: 'section', surveyId: doc.surveyId, sectionId: section.id });

  return {
    key: section.id,
    href,
    title: section.title,
    lamp,
    ...(reason !== undefined ? { reason } : {}),
    notes: args.notesBySection.get(section.id) ?? 0,
    current,
    done,
  };
}

export function mapModel(args: {
  doc: SurveyDocument;
  results: SurveyResults;
  walked: ReadonlySet<string>;
  surveyTitle: string;
  route: Route;
}): MapModel {
  const { doc, results, walked, surveyTitle, route } = args;
  const currentKey = currentKeyFor(route);

  const notesBySection = new Map<string, number>();
  for (const note of doc.notes) {
    notesBySection.set(note.sectionInstanceId, (notesBySection.get(note.sectionInstanceId) ?? 0) + 1);
  }

  const shared = { doc, results, walked, currentKey, notesBySection };
  const row = (section: SectionInstance): MapRowModel => rowFor({ ...shared, section });

  // Walk `results.sections` once, folding each room's consecutive run of
  // sections into a branch — the engine's order IS the tree's order.
  const nodes: MapNode[] = [];
  const roomsById = new Map(doc.rooms.map((room, index) => [room.id, { room, index }]));
  for (const section of results.sections) {
    const roomId = section.scope.roomId;
    if (!roomId) {
      nodes.push({ kind: 'row', row: row(section) });
      continue;
    }
    const last = nodes.at(-1);
    if (last?.kind === 'branch' && last.branch.roomId === roomId) {
      last.branch.rows.push(row(section));
      continue;
    }
    const entry = roomsById.get(roomId);
    nodes.push({
      kind: 'branch',
      branch: {
        roomId,
        name: entry?.room.name ?? 'Machine room',
        position: (entry?.index ?? 0) + 1,
        rows: [row(section)],
        done: 0,
        total: 0,
      },
    });
  }

  // Declared rooms that do not exist yet — B12 above the room count — are on
  // the map from the moment the number is tapped (D-037): one hollow row each,
  // named as the room will be, opening the Room Setup that creates it under the
  // SAME id Start Survey mints (`nextRoomId`), so the two doors open one room.
  // After the last room branch, before Fire Life Safety; before any branch
  // exists, right after Building.
  const slots = pendingRoomSlots(doc);
  const startingSlot =
    route.name === 'roomSetup' ? slots.find((slot) => slot.roomId === route.roomId) : undefined;
  if (slots.length > 0) {
    const pendingRows: MapNode[] = slots.map((slot) => {
      const current = slot === startingSlot;
      return {
        kind: 'row',
        row: {
          key: `pending:${slot.roomId}`,
          href: hrefFor({ name: 'roomSetup', surveyId: doc.surveyId, roomId: slot.roomId }),
          title: slot.name,
          lamp: current ? 'here' : 'todo',
          notes: 0,
          current,
          done: false,
        },
      };
    });
    const lastBranch = nodes.map((n) => n.kind).lastIndexOf('branch');
    const building = nodes.findIndex(
      (n) => n.kind === 'row' && n.row.key === sectionInstanceId('building'),
    );
    nodes.splice(lastBranch >= 0 ? lastBranch + 1 : Math.max(0, building + 1), 0, ...pendingRows);
  }

  // Review & Submit — a destination, not a submit control. The close screen
  // still gates (not-ready face → last look → Submit), so showing the row is
  // not progressive-disclosure regression; it is the way to the reckoning.
  const submitted = doc.status === 'submitted_clean' || doc.status === 'submitted_with_gaps';
  const submitRow: MapRowModel = {
    key: 'submit',
    href: hrefFor({ name: 'submit', surveyId: doc.surveyId }),
    title: 'Review & Submit',
    lamp: currentKey === 'submit' ? 'here' : submitted ? 'done' : 'todo',
    notes: 0,
    current: currentKey === 'submit',
    done: submitted,
  };
  // The submit row sits after Ad Hoc; `results.sections` ends with adhoc.
  nodes.push({ kind: 'row', row: submitRow });

  for (const node of nodes) {
    if (node.kind !== 'branch') continue;
    node.branch.total = node.branch.rows.length;
    node.branch.done = node.branch.rows.filter((r) => r.done).length;
  }

  const countable = nodes.flatMap((n) =>
    n.kind === 'branch' ? n.branch.rows : n.row.key === 'submit' ? [] : [n.row],
  );
  const done = countable.filter((r) => r.done).length;
  const total = countable.length;
  const pct = total === 0 ? 0 : Math.round((100 * done) / total);

  // The bar: where you are, in the surveyor's own names.
  const currentSection = currentKey ? results.sectionById.get(currentKey) : undefined;
  const roomOfCurrent = currentSection?.scope.roomId
    ? doc.rooms.find((r) => r.id === currentSection.scope.roomId)
    : route.name === 'sections'
      ? doc.rooms.find((r) => r.id === route.roomId)
      : undefined;
  const here =
    currentKey === 'submit'
      ? 'Review & Submit'
      : startingSlot
        ? 'Room Setup'
        : (currentSection?.title ??
          (route.name === 'sections' ? (roomOfCurrent?.name ?? 'Survey Map') : 'Survey Map'));
  const room = roomOfCurrent?.name ?? startingSlot?.name;
  const bar = { here, place: surveyTitle, room: room ? shortRoomName(room) : 'Survey' };

  return {
    surveyTitle,
    nodes,
    progress: { done, total, pct },
    bar,
    currentKey,
    ready: guidePlan(results, walked).ready,
  };
}
